import { useRef, useLayoutEffect, useState } from "react";
import { useDefaultShakhsSherkat } from "context/ShakhsContext";
import Clock from "./Clock";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Text,
  Tooltip,
  useColorMode,
  useToken,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { TimeIcon, CalendarIcon, SunIcon, MoonIcon } from "@chakra-ui/icons";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { isDeveloper } from "utils/permissions";
import { UserIcon } from "components/Icons/Icons";
import { useThemeMode } from "context/ThemeContext";
import { ThemeIcon } from "components/Icons/Icons";
import { LogoutIcon } from "components/Icons/Icons";
import { userSignOut } from "appRedux/actions";
import { Link } from "react-router-dom";

// متن رو داخل باکس، بدون wrap، تا جایی که لازمه کوچیک می‌کنه تا کامل جا بشه
const useShrinkToFit = (text, { min = 6, max = 10, safety = 0.96 } = {}) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const measurerRef = useRef(null);
  const [fontSize, setFontSize] = useState(max);

  useLayoutEffect(() => {
    const containerEl = containerRef.current;
    const textEl = textRef.current;
    if (!containerEl || !textEl) return;

    // یک المان مخفیِ جدا برای اندازه‌گیری می‌سازیم تا اصلاً به استایل
    // المان اصلی دست نزنیم (چون style مستقیم روی المان، بعداً روی کلاسی
    // که Chakra برای فونت جدید می‌سازه اولویت پیدا می‌کنه و اجازه‌ی
    // کوچیک‌شدن واقعی رو نمی‌ده)
    if (!measurerRef.current) {
      const span = document.createElement("span");
      span.style.position = "fixed";
      span.style.top = "-9999px";
      span.style.left = "-9999px";
      span.style.visibility = "hidden";
      span.style.pointerEvents = "none";
      span.style.whiteSpace = "nowrap";
      document.body.appendChild(span);
      measurerRef.current = span;
    }
    const measurer = measurerRef.current;

    const fit = () => {
      const computed = window.getComputedStyle(textEl);
      measurer.style.fontFamily = computed.fontFamily;
      measurer.style.fontWeight = computed.fontWeight;
      measurer.style.letterSpacing = computed.letterSpacing;
      measurer.style.fontSize = `${max}px`;
      measurer.textContent = text || "";

      const available = containerEl.clientWidth;
      const needed = measurer.scrollWidth;

      if (available > 0 && needed > available) {
        // یه حاشیه‌ی امنِ کوچیک (safety) برای گرد شدن ساب‌پیکسلی که باعث
        // می‌شد گاهی دقیقاً یک کاراکتر آخر جا نشه
        const ratio = (available / needed) * safety;
        const next = Math.max(min, Math.floor(max * ratio * 100) / 100);
        setFontSize(next);
      } else {
        setFontSize(max);
      }
    };

    fit();
    // اگر فونت فارسی دیرتر لود بشه، اندازه‌گیری اولیه رو دوباره انجام می‌دیم
    document.fonts?.ready?.then(fit);

    const resizeObserver = new ResizeObserver(fit);
    resizeObserver.observe(containerEl);
    return () => resizeObserver.disconnect();
  }, [text, min, max, safety]);

  useLayoutEffect(() => {
    return () => {
      measurerRef.current?.remove();
      measurerRef.current = null;
    };
  }, []);

  return { containerRef, textRef, fontSize };
};

const InfoPanel = ({ onCloseDrawer }) => {
  const authStore = useSelector((store) => store.auth, shallowEqual);
  const { authUser } = authStore;
  const { defaultShakhs } = useDefaultShakhsSherkat();
  const dev = isDeveloper(authUser);
  const [buttonsShadow] = useToken("colors", ["navbar_logout_button_shadow"]);
  const [LogOutBorder] = useToken("colors", ["navbar_logout_button_color"]);

  /*   const { colorMode, toggleColorMode } = useColorMode(); */
  const { colorMode } = useColorMode();
  const dispatch = useDispatch();
  const { themeMode, setThemeMode } = useThemeMode();

  // منطق واقعی تعویض تم (بدون افکت)
  const applyNextTheme = () => {
    switch (themeMode) {
      case "light":
        setThemeMode("dark");
        break;
      case "dark":
        setThemeMode("system");
        break;
      default:
        setThemeMode("light");
    }
  };

  // کلیک روی دکمه‌ی تم: اگر مرورگر از View Transition API پشتیبانی کنه،
  // یه دایره از نقطه‌ی کلیک باز میشه و کل صفحه رو به تم جدید می‌بره.
  // اگر پشتیبانی نشه (مثلاً فایرفاکس/سافاری قدیمی)، بی‌صدا و فوری عوض میشه.
  const nextTheme = (event) => {
    if (!document.startViewTransition) {
      applyNextTheme();
      return;
    }

    const x = event.clientX;
    const y = event.clientY;
    const endRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y),
    );

    const transition = document.startViewTransition(() => {
      applyNextTheme();
    });

    transition.ready.then(() => {
      document.documentElement.animate(
        {
          clipPath: [
            `circle(0px at ${x}px ${y}px)`,
            `circle(${endRadius}px at ${x}px ${y}px)`,
          ],
        },
        {
          duration: 400,
          easing: "ease-in-out",
          pseudoElement: "::view-transition-new(root)",
        },
      );
    });
  };

  const handleSignOut = () => {
    dispatch(userSignOut());
  };
  const fullName =
    `${authUser?.firstName || ""} ${authUser?.lastName || ""}`.trim();

  const {
    containerRef: dateContainerRef,
    textRef: dateTextRef,
    fontSize: dateFontSize,
  } = useShrinkToFit(defaultShakhs.shamsiToday, { min: 7, max: 10 });

  return (
    <Box mb={2} mt={1} px="16px" py="14px">
      {/* لوگو و عنوان */}
      <Flex align="center" gap="10px" mb="14px">
        <Flex
          align="center"
          justify="center"
          w="40px"
          h="40px"
          borderRadius="8px"
        >
          <img src="/icons/loginregisterIranmovadiLogo.png" width={"full"} />
        </Flex>
        <Flex w={"full"} justifyContent={"space-between"}>
          <Box>
            <Text fontSize="15px" fontWeight="bold" color="white">
              ایـــــــران مـــــــــؤدی
            </Text>
            <Text fontSize="12px" fontWeight="semi-bold" color="white">
              سامانه جامع مودیان
            </Text>
          </Box>
          <HStack>
            <Tooltip
              hasArrow
              placement="bottom"
              label={
                themeMode === "light"
                  ? "روشن"
                  : themeMode === "dark"
                    ? "تاریک"
                    : "سیستم"
              }
            >
              <IconButton
                as={motion.button}
                whileTap={{ scale: 0.88 }}
                bg="infopanel_theme_bg"
                position={"relative"}
                variant={"solid"}
                h={"40.5px"}
                minW={"40.5px"}
                size="sm"
                borderRadius="8px"
                _hover={{
                  bg: "infopanel_theme_bg",
                }}
                icon={
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                      key={themeMode}
                      initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
                      animate={{ opacity: 1, rotate: 0, scale: 1 }}
                      exit={{ opacity: 0, rotate: 90, scale: 0.5 }}
                      transition={{ duration: 0.22 }}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {themeMode === "light" ? (
                        <SunIcon color="orange.400" />
                      ) : themeMode === "dark" ? (
                        <MoonIcon color="yellow.300" />
                      ) : (
                        <ThemeIcon w="20px" h="20px" />
                      )}
                    </motion.span>
                  </AnimatePresence>
                }
                onClick={nextTheme}
                aria-label="تغییر تم"
              />
            </Tooltip>

            <Button
              fontSize="12px"
              borderRadius={8}
              w="40.5px"
              h="40.5px"
              fontWeight="bold"
              color="text"
              size="md"
              display={{ base: "flex", lg: "none" }}
              bg="infopanel_theme_bg"
              onClick={onCloseDrawer}
            >
              ✕
            </Button>
          </HStack>
        </Flex>
      </Flex>
      {/* جستجو */}
      {/* <InputGroup size="sm" mb="12px">
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.400" boxSize="3" />
        </InputLeftElement>
        <Input
          placeholder="جستجو در منو..."
          fontSize="13px"
          borderRadius="8px"
          bg="gray.50"
          border="none"
        />
      </InputGroup> */}

      {/* کاربر */}
      <Flex
        align="center"
        gap="10px"
        bg="infopanel_card"
        borderRadius="8px"
        p="10px"
        mb="10px"
      >
        {/* <Avatar size="sm" name={fullName} bg="teal.100" color="teal.800" /> */}
        <Tooltip placement="bottom" label="حساب کاربری">
          <Box
            pointerEvents={"pointer"}
            bg="infopanel_avatar_bg"
            as={Link}
            to="/user/profile"
            py={1}
            px={2}
            rounded={"full"}
          >
            <UserIcon color="infopanel_avatar_color" />
          </Box>
        </Tooltip>
        <Box flex="1" minW="0">
          <Text fontSize="13px" fontWeight="500" color="text" noOfLines={1}>
            {fullName || "کاربر مهمان"}
          </Text>
          <Text fontSize="11px" color="infopanel_role_text">
            کاربر سامانه
          </Text>
        </Box>

        {/* <Tooltip label="حساب توسعه‌دهنده" hasArrow>
          <Badge
            colorScheme="teal"
            bg="infopanel_avatar_bg"
            display="flex"
            alignItems="center"
            borderRadius="6px"
            px="6px"
            py="4px"
          >
            <SettingsIcon color="infopanel_avatar_color" boxSize="10px" />
          </Badge>
        </Tooltip> */}

        <Tooltip label="خروج از حساب کاربری" hasArrow>
          <Badge
            onClick={handleSignOut}
            w={"32px"}
            h={"32px"}
            cursor={"pointer"}
            colorScheme="red"
            bg="navbar_logout_button_bg"
            border={LogOutBorder}
            display="flex"
            boxShadow={buttonsShadow}
            alignItems="center"
            borderRadius="4px"
            px="6px"
            py="4px"
          >
            <LogoutIcon
              position={"relative"}
              right={"3px"}
              color="navbar_logout_button_icon"
              boxSize="13px"
            />
          </Badge>
        </Tooltip>
      </Flex>

      {/* تاریخ و ساعت */}
      <Flex gap="8px">
        <Flex
          flex="2"
          align="center"
          gap="6px"
          bg="infopanel_card"
          borderRadius="6px"
          maxW="150px"
          px="10px"
          py="7px"
          minW={0}
        >
          <CalendarIcon
            color="infopanel_avatar_color"
            boxSize="3"
            flexShrink={0}
          />

          <Box ref={dateContainerRef} flex="1" minW={0} overflow="hidden">
            <Text
              ref={dateTextRef}
              fontSize={`${dateFontSize}px`}
              color="infopanel_data_text"
              whiteSpace="nowrap"
              display="inline-block"
            >
              {defaultShakhs.shamsiToday}
            </Text>
          </Box>
        </Flex>
        <Flex
          flex="1"
          align="center"
          gap="6px"
          bg="infopanel_card"
          borderRadius="6px"
          px="10px"
          py="7px"
          maxW={"80px"}
        >
          <TimeIcon color="infopanel_avatar_color" boxSize="3" />
          <Text fontSize="11px" color="infopanel_data_text">
            <Clock />
          </Text>
        </Flex>
      </Flex>
      
    </Box>
    
  );
};

export default InfoPanel;
